// AgentRulesView — Settings → Profile "Regras do Agente" (FR, daily-auto-improve).
//
// The user picks which canonical software-engineering rule books (ciembor/agent-rules-books)
// the agent must follow. The active set is injected into the Code-tab agent AND the daily
// self-improve run, so "the scope always follows the best practices". Native <dialog> +
// framer-motion (mirrors AppearanceView's modal contract). Selection persists via
// agentRulesPrefs (localStorage + ~/.apolo mirror for the Node daily-improve script).

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenCheck, X, RotateCcw, Check } from 'lucide-react';
import { listBooks, defaultActiveSlugs, assembleRulesBlock, DEFAULT_VARIANT } from '../../../shared/agent-rules/registry.mjs';
import { getActiveRules, setActiveRules } from '../lib/agentRulesPrefs.js';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };
const BOOKS = listBooks();

function Segmented({ ariaLabel, options, value, onChange }) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 ${
              active ? 'bg-apolo-accent text-white' : 'text-apolo-textSecondary hover:bg-apolo-surface1 hover:text-apolo-textPrimary'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function BookRow({ book, checked, variant, onToggle }) {
  const kb = Math.round((book.sizes[variant] || 0) / 1024 * 10) / 10;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onToggle(book.slug)}
      className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 ${
        checked ? 'border-apolo-accent/40 bg-apolo-accent/5' : 'border-apolo-borderSubtle bg-apolo-surface2 hover:bg-apolo-surface1'
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${
          checked ? 'border-apolo-accent bg-apolo-accent text-white' : 'border-apolo-borderStrong bg-apolo-surface1'
        }`}
      >
        {checked ? <Check size={11} strokeWidth={3} /> : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-apolo-textPrimary">{book.title}</span>
          <span className="shrink-0 text-[10px] tabular-nums text-apolo-textTertiary">{book.author} · {kb}KB</span>
        </span>
        <span className="text-xs text-apolo-textTertiary">{book.blurb}</span>
      </span>
    </button>
  );
}

export default function AgentRulesView({ open, onClose }) {
  const ref = useRef(null);
  const [variant, setVariant] = useState(DEFAULT_VARIANT);
  const [active, setActive] = useState([]);

  // Load the current selection each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const cur = getActiveRules();
    setActive(cur.activeSlugs);
    setVariant(cur.variant);
  }, [open]);

  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    if (!open && ref.current && ref.current.open) ref.current.close();
  }, [open]);

  // Persist on every change (live — the agent picks it up next turn).
  const commit = (nextActive, nextVariant) => {
    setActive(nextActive);
    setVariant(nextVariant);
    setActiveRules({ activeSlugs: nextActive, variant: nextVariant });
  };
  const toggle = (slug) => {
    const next = active.includes(slug) ? active.filter((s) => s !== slug) : [...active, slug];
    commit(next, variant);
  };
  const reset = () => commit(defaultActiveSlugs(), DEFAULT_VARIANT);

  const preview = useMemo(
    () => assembleRulesBlock({ activeSlugs: active, variant }),
    [active, variant],
  );
  const droppedCount = preview.dropped.length;

  return (
    <dialog
      ref={ref}
      aria-labelledby="agent-rules-modal-title"
      onClose={onClose}
      className="rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary shadow-2xl backdrop:bg-apolo-overlay backdrop:backdrop-blur-sm"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ scale: 0.97, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 8 }}
            transition={SPRING}
            className="flex max-h-[85vh] w-[min(34rem,100vw)] flex-col"
          >
            <header className="flex items-center justify-between border-b border-apolo-borderSubtle p-4">
              <div className="flex items-center gap-2">
                <BookOpenCheck size={18} className="shrink-0 text-apolo-accent" aria-hidden="true" />
                <h2 id="agent-rules-modal-title" className="text-base font-semibold">Regras do Agente</h2>
              </div>
              <button
                type="button"
                aria-label="Fechar"
                onClick={onClose}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accentRing"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </header>

            <div className="flex items-center justify-between gap-3 border-b border-apolo-borderSubtle px-4 py-3">
              <p className="text-xs text-apolo-textTertiary">
                As práticas dos livros escolhidos guiam como o agente muda código (Code + auto-melhoria diária).
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-apolo-textSecondary">Profundidade</span>
                <Segmented
                  ariaLabel="Profundidade das regras"
                  value={variant}
                  onChange={(v) => commit(active, v)}
                  options={[{ value: 'nano', label: 'Nano (compacto)' }, { value: 'mini', label: 'Mini (detalhado)' }]}
                />
              </div>
              <button
                type="button"
                onClick={reset}
                title="Restaurar o conjunto padrão"
                className="inline-flex items-center gap-1.5 rounded-md border border-apolo-borderSubtle px-2.5 py-1.5 text-xs text-apolo-textTertiary transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
              >
                <RotateCcw size={12} aria-hidden="true" /> Padrão
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
              <div className="flex flex-col gap-2">
                {BOOKS.map((book) => (
                  <BookRow
                    key={book.slug}
                    book={book}
                    variant={variant}
                    checked={active.includes(book.slug)}
                    onToggle={toggle}
                  />
                ))}
              </div>
            </div>

            <footer className="border-t border-apolo-borderSubtle px-4 py-3 text-xs text-apolo-textTertiary">
              {preview.included.length === 0 ? (
                <span>Nenhuma regra ativa — o agente usa só o comportamento padrão.</span>
              ) : (
                <span>
                  <strong className="text-apolo-textSecondary">{preview.included.length}</strong> livro(s) ativos ·{' '}
                  <span className="tabular-nums">{Math.round(preview.chars / 1024 * 10) / 10}KB</span> injetados no prompt
                  {droppedCount > 0 && (
                    <span className="text-apolo-warning"> · {droppedCount} fora do orçamento (use Nano ou desmarque alguns)</span>
                  )}
                </span>
              )}
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </dialog>
  );
}
